"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import { REBOTT_API_LOCATION_BASE } from "@/config";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
  Circle,
} from "@react-google-maps/api";

// โครงข้อมูลกลาง
interface RebottItem {
  id: number;
  location: string;
  bannerImages: { imageUrl: string }[];
  campaignTitle: string;
  publishDate: string;
  expireDate: string;
  latitude: string;
  longitude: string;
  lat?: number;
  lng?: number;
}

interface LocationApiCampaign {
  name?: string;
  imageKey?: string;
  startDate?: string;
  endDate?: string;
}

interface LocationApiRecord {
  name?: string;
  imageKey?: string;
  campaigns?: LocationApiCampaign[];
  location?: {
    name?: string;
    latitude?: string | number;
    longitude?: string | number;
  } | null;
}

interface LocationApiResponse {
  total: number;
  data: LocationApiRecord[];
}

const containerStyle = { width: "100%", height: "600px" };
const LOCATION_API_ENDPOINT = "/api/rebott-locations";
const LOCATION_API_ORIGIN = new URL(REBOTT_API_LOCATION_BASE).origin;
const defaultCenter = { lat: 13.736717, lng: 100.523186 }; // กรุงเทพฯ

const inThailand = (lat: number, lng: number) =>
  lat >= 5 && lat <= 21 && lng >= 97 && lng <= 106;

const parseDate = (s?: string) => (s ? new Date(s) : undefined);

function toResourceUrl(imageKey?: string): string {
  if (!imageKey) return "";
  if (/^https?:\/\//i.test(imageKey)) return imageKey;
  const normalizedKey = imageKey.replace(/^\/+/, "");
  return `${LOCATION_API_ORIGIN}/resource/${normalizedKey}`;
}

function isActive(item: { publishDate?: string; expireDate?: string }) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const pub = parseDate(item.publishDate);
  const exp = parseDate(item.expireDate);
  const started = !pub || pub <= now;
  const notEnded = !exp || exp >= startOfToday;
  return started && notEnded;
}

type RawItem = Partial<RebottItem> & { raw?: any };
type GoodItem = RebottItem & { lat: number; lng: number };
type BadItem = { item: RawItem; reason: string; parsed?: { lat?: number; lng?: number } };

function sanitizeCoordinates(rawItems: RawItem[]) {
  const valid: GoodItem[] = [];
  const invalid: BadItem[] = [];
  for (const r of rawItems) {
    let rawLat: any =
      (r as any).latitude ??
      (r as any).rvmLatitude ??
      (r as any).rvmMachineLocation?.latitude;
    let rawLng: any =
      (r as any).longitude ??
      (r as any).rvmLongitude ??
      (r as any).rvmMachineLocation?.longitude;

    if (rawLat == null && rawLng == null) {
      invalid.push({ item: r, reason: "no-lat-lng" });
      continue;
    }

    const sLat = String(rawLat ?? "").trim().replace(",", ".");
    const sLng = String(rawLng ?? "").trim().replace(",", ".");
    const latNum = Number.parseFloat(sLat);
    const lngNum = Number.parseFloat(sLng);
    const parsed = {
      lat: Number.isFinite(latNum) ? latNum : undefined,
      lng: Number.isFinite(lngNum) ? lngNum : undefined,
    };

    if (parsed.lat === undefined || parsed.lng === undefined) {
      invalid.push({ item: r, reason: "parse-failed", parsed });
      continue;
    }
    if (parsed.lat === 0 && parsed.lng === 0) {
      invalid.push({ item: r, reason: "zero-zero", parsed });
      continue;
    }

    const latInRange = parsed.lat >= -90 && parsed.lat <= 90;
    const lngInRange = parsed.lng >= -180 && parsed.lng <= 180;

    if (latInRange && lngInRange) {
      valid.push({ ...(r as any), lat: parsed.lat, lng: parsed.lng } as GoodItem);
      continue;
    }

    // heuristic: ลอง swap
    const swapLatCandidate = parsed.lng!;
    const swapLngCandidate = parsed.lat!;
    const swapLatInRange = swapLatCandidate >= -90 && swapLatCandidate <= 90;
    const swapLngInRange = swapLngCandidate >= -180 && swapLngCandidate <= 180;

    if (!latInRange && swapLatInRange && swapLngInRange) {
      valid.push({ ...(r as any), lat: swapLatCandidate, lng: swapLngCandidate } as GoodItem);
      invalid.push({ item: r, reason: "swapped-lng-lat-heuristic", parsed });
      continue;
    }

    invalid.push({
      item: r,
      reason: `out-of-range (lat=${parsed.lat}, lng=${parsed.lng})`,
      parsed,
    });
  }
  return { valid, invalid };
}

export default function RebottMapPage() {
  const [items, setItems] = useState<GoodItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<GoodItem | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  // 🔵 Marker รูป Rebott — รักษาอัตราส่วนจริง
  const MARKER_URL = "/image/rebottmarker-sm.webp";
  const MAX_MARKER_HEIGHT = 96;
  const [markerSize, setMarkerSize] = useState<{ w: number; h: number } | null>(null);

  // 🧭 ตำแหน่งผู้ใช้
  const [userPos, setUserPos] = useState<google.maps.LatLngLiteral | null>(null);
  const [userAccuracy, setUserAccuracy] = useState<number>(0);
  const [locating, setLocating] = useState(false);

  // ตำแหน่งแนวตั้งของปุ่ม "ตำแหน่งของฉัน" ให้เท่ากับ Map|Satellite
  const [mapTopOffset, setMapTopOffset] = useState(12);
  const SAFE_RIGHT = 64; // กันชนไม่ให้ทับปุ่มขยายมุมขวาบน

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  // โหลดขนาดรูป marker เพื่อคงสัดส่วน
  useEffect(() => {
    const img = new Image();
    img.src = MARKER_URL;
    img.onload = () => {
      const naturalW = img.naturalWidth || img.width;
      const naturalH = img.naturalHeight || img.height;
      const targetH = Math.min(naturalH, MAX_MARKER_HEIGHT);
      const targetW = Math.round((naturalW / naturalH) * targetH);
      setMarkerSize({ w: targetW, h: targetH });
    };
  }, []);

  // ดึงตำแหน่ง top ของคอนโทรล Map|Satellite จริง ๆ
  const updateMyButtonTop = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const mapDiv = map.getDiv();
    const mtc = mapDiv.querySelector(".gm-style-mtc") as HTMLElement | null; // Google Maps control container
    if (!mtc) return; // ถ้ายังไม่พร้อม ใช้ค่าเดิมไปก่อน
    const mapRect = mapDiv.getBoundingClientRect();
    const mtcRect = mtc.getBoundingClientRect();
    const top = Math.max(0, Math.round(mtcRect.top - mapRect.top));
    setMapTopOffset((prev) => (prev === top ? prev : top));
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    const mapDiv = mapRef.current.getDiv();

    // อัปเดตเมื่อ resize/DOM ของแผนที่เปลี่ยน
    const onResize = () => updateMyButtonTop();
    window.addEventListener("resize", onResize);

    const mo = new MutationObserver(updateMyButtonTop);
    mo.observe(mapDiv, { childList: true, subtree: true });

    // อัปเดตครั้งแรก
    updateMyButtonTop();

    return () => {
      window.removeEventListener("resize", onResize);
      mo.disconnect();
    };
  }, [isLoaded, updateMyButtonTop]);

  useEffect(() => {
    let cancelled = false;

    async function loadLocations() {
      setLoading(true);
      try {
        const url = new URL(LOCATION_API_ENDPOINT, window.location.origin);
        url.searchParams.set("pageNumber", "1");
        url.searchParams.set("pageSize", "1000");

        const response = await fetch(url.toString());
        if (!response.ok) {
          if (!cancelled) setItems([]);
          return;
        }

        const data = (await response.json()) as LocationApiResponse;
        const records = Array.isArray(data?.data) ? data.data : [];

        const mapped: RawItem[] = records.flatMap((record, recordIndex) => {
          const locationName = record.location?.name || record.name || "No location";
          const latitude =
            record.location?.latitude != null ? String(record.location.latitude) : "";
          const longitude =
            record.location?.longitude != null ? String(record.location.longitude) : "";
          const campaigns = record.campaigns?.length ? record.campaigns : [{}];

          return campaigns.map((campaign, campaignIndex) => {
            const bannerImages = [campaign.imageKey, record.imageKey]
              .filter((key): key is string => Boolean(key))
              .map((key) => ({ imageUrl: toResourceUrl(key) }));

            return {
              id: recordIndex * 1000 + campaignIndex + 1,
              location: locationName,
              bannerImages,
              campaignTitle: campaign.name || "",
              publishDate: campaign.startDate || "",
              expireDate: campaign.endDate || "",
              latitude,
              longitude,
              raw: record,
            } as RawItem;
          });
        });

        const { valid, invalid } = sanitizeCoordinates(mapped);
        let filtered = valid.filter((v) => inThailand(v.lat, v.lng));
        filtered = filtered.filter((v) => isActive(v));

        if (!cancelled) {
          setItems(filtered);
          if (invalid.length) console.warn(`Sanitize invalid: ${invalid.length}`);
        }
      } catch (err) {
        console.error("loadLocations error", err);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadLocations();
    return () => {
      cancelled = true;
    };
  }, []);

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    fitBoundsToMarkers(map, items);
    updateMyButtonTop(); // ให้ปุ่มยึดตำแหน่งทันทีที่แผนที่โหลด
  };

  useEffect(() => {
    if (mapRef.current) fitBoundsToMarkers(mapRef.current, items);
  }, [items]);

  function fitBoundsToMarkers(map: google.maps.Map, data: GoodItem[]) {
    if (!data || data.length === 0) {
      map.setCenter(defaultCenter);
      map.setZoom(6);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    let any = false;
    for (const it of data) {
      const { lat, lng } = it;
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        bounds.extend({ lat, lng });
        any = true;
      }
    }
    if (any) map.fitBounds(bounds, 80);
    else {
      map.setCenter(defaultCenter);
      map.setZoom(6);
    }
  }

  // 👉 ฟังก์ชันหาตำแหน่งปัจจุบันผู้ใช้
  const locateMe = () => {
    if (!navigator.geolocation) {
      alert("เบราว์เซอร์ไม่รองรับการระบุตำแหน่ง");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const p = { lat: latitude, lng: longitude };
        setUserPos(p);
        setUserAccuracy(accuracy || 0);
        if (mapRef.current) {
          mapRef.current.panTo(p);
          if ((mapRef.current.getZoom() || 0) < 14) mapRef.current.setZoom(14);
        }
        setLocating(false);
      },
      (err) => {
        console.error("geolocation error", err);
        alert("เปิดตำแหน่งไม่สำเร็จ: " + err.message);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  if (loadError) return <div>Load Google Maps error</div>;
  if (!isLoaded) return <div>Loading Google Maps...</div>;

  return (
    <div className="space-y-6">
      <div className="text-center mt-20">
        <h2 className="text-2xl font-semibold mb-3">
          ตำแหน่งตู้ <span className="text-[#2998E2]">Rebott</span> ของเรา
        </h2>
        <div className="inline-block bg-[#2998E2] text-white text-sm px-4 py-1 rounded-lg">
          จำนวน: {items.length} จุดบริการ
        </div>
      </div>

      <div className="rounded-lg overflow-hidden shadow-md">
        <GoogleMap
          mapContainerStyle={containerStyle}
          mapContainerClassName="rebott-map"
          center={defaultCenter}
          zoom={6}
          onLoad={onMapLoad}
        >
          {/* ปุ่มตำแหน่งของฉัน — จัดให้อยู่ระดับเดียวกับ Map|Satellite และไม่ทับปุ่มขยาย */}
          <div
            style={{
              position: "absolute",
              right: SAFE_RIGHT,
              top: mapTopOffset,
              zIndex: 2,
            }}
          >
            <button
              onClick={locateMe}
              disabled={locating}
              aria-label="ตำแหน่งของฉัน"
              style={{
                background: "#111827",
                color: "white",
                border: "none",
                borderRadius: 2,
                padding: "10px 14px",
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "0.8px",
                lineHeight: 1,
                minWidth: 150,
                boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                cursor: locating ? "default" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                whiteSpace: "nowrap",
                wordBreak: "keep-all",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M12 2C8.134 2 5 5.134 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7zm0 10.5a3.5 3.5 0 110-7 3.5 3.5 0 010 7z"/>
              </svg>
              <span>{locating ? "กำลังค้นหา..." : "ตำแหน่งของฉัน"}</span>
            </button>
          </div>

          {/* ตำแหน่งผู้ใช้ */}
          {userPos && (
            <>
              <Marker
                position={userPos}
                zIndex={2000}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: "#2563eb",
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 2,
                }}
              />
              {userAccuracy > 0 && (
                <Circle
                  center={userPos}
                  radius={userAccuracy}
                  options={{
                    fillColor: "#60a5fa",
                    fillOpacity: 0.2,
                    strokeColor: "#3b82f6",
                    strokeOpacity: 0.5,
                    strokeWeight: 1,
                    clickable: false,
                    editable: false,
                    zIndex: 1500,
                  }}
                />
              )}
            </>
          )}

          {/* ตำแหน่งตู้ Rebott */}
          {items.map((it) => {
            const lat = Number(it.lat);
            const lng = Number(it.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

            return (
              <Marker
                key={`${it.id}-${lat}-${lng}`}
                position={{ lat, lng }}
                title={it.location || it.campaignTitle || "Rebott"}
                onClick={() => setSelected(it)}
                zIndex={selected?.id === it.id ? 1000 : 1}
                icon={
                  markerSize
                    ? {
                        url: MARKER_URL,
                        scaledSize: new google.maps.Size(markerSize.w, markerSize.h),
                        origin: new google.maps.Point(0, 0),
                        anchor: new google.maps.Point(markerSize.w / 2, markerSize.h),
                      }
                    : undefined
                }
              />
            );
          })}

          {selected &&
            (() => {
              const lat = Number(selected.lat);
              const lng = Number(selected.lng);
              if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
              const img =
                selected.bannerImages && selected.bannerImages.length > 0
                  ? selected.bannerImages[0].imageUrl
                  : undefined;
              return (
                <InfoWindow
                  position={{ lat, lng }}
                  onCloseClick={() => setSelected(null)}
                  options={{
                    pixelOffset: new google.maps.Size(0, -(markerSize?.h ?? 56)),
                  }}
                >
                  <div style={{ maxWidth: 260, textAlign: "center", lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 900, marginBottom: 4 }}>
                      <span className="text-[#2998E2]">พิกัด</span>
                    </div>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>
                      {selected.location}
                    </div>
                    {img && (
                      <img
                        src={img}
                        alt={selected.location}
                        style={{
                          width: "100%",
                          height: 120,
                          objectFit: "cover",
                          borderRadius: 6,
                          marginBottom: 6,
                        }}
                      />
                    )}
                    <div style={{ fontSize: 13, color: "#444", marginBottom: 8 }}>
                      <span className="text-[#2998E2]">แคมเปญ: </span>
                      {selected.campaignTitle}
                    </div>
                    <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                      <a
                        href={`https://maps.google.com/?q=${lat},${lng}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          textDecoration: "none",
                          color: "white",
                          background: "#1e40af",
                          padding: "6px 8px",
                          borderRadius: 6,
                          fontSize: 13,
                        }}
                      >
                        เปิดใน Google Maps
                      </a>
                      <button
                        style={{
                          background: "#e5e7eb",
                          borderRadius: 6,
                          padding: "6px 8px",
                          border: "none",
                          fontSize: 13,
                        }}
                        onClick={() => setSelected(null)}
                      >
                        ปิด
                      </button>
                    </div>
                  </div>
                </InfoWindow>
              );
            })()}
        </GoogleMap>
      </div>

      {loading && (
        <div className="text-center text-sm text-gray-500">กำลังโหลดข้อมูล...</div>
      )}
    </div>
  );
}

